import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import useSEO from '../hooks/useSEO';

function InnovationHub({ logo }) {
  useSEO({
    title: 'Innovation Hub',
    description: 'Research and insights on simulation, AI, system dynamics, and decision intelligence from the Veydra team.'
  });

  const articles = [
    {
      category: 'Article',
      title: 'System Dynamics in Enterprise Decision Making',
      excerpt: 'Foundational research on applying system dynamics to complex business environments. Learn how feedback-driven modeling helps executives understand the long-term consequences of strategic choices.',
      href: '/innovation/system-dynamics'
    },
    {
      category: 'Article',
      title: 'AI-Enhanced Simulation Modeling',
      excerpt: 'How machine learning augments traditional simulation approaches for better predictions. Explore the intersection of neural networks and causal modeling for next-generation decision support.',
      href: '/innovation/ai-simulation'
    },
    {
      category: 'Paper',
      title: 'Code-First Methodology for Enterprise Simulation',
      excerpt: 'A practical framework for building robust simulation systems by treating models as executable, testable artifacts from day one.',
      href: '/papers/code-first-methodology'
    },
    {
      category: 'Article',
      title: 'Quantifying Uncertainty in Strategic Models',
      excerpt: 'Methods for incorporating and communicating uncertainty in business simulations. A rigorous framework for probabilistic scenario analysis that decision-makers can trust.',
      href: '/innovation/uncertainty',
      thumbnail: '/images/uncertainty-square.png'
    },
    {
      category: 'Article',
      title: 'Feedback Loops in Business Strategy',
      excerpt: 'Understanding and modeling the reinforcing patterns that drive organizational outcomes. From market dynamics to operational bottlenecks—see how loops shape your business.',
      href: '/innovation/feedback-loops'
    },
    {
      category: 'Article',
      title: 'AI Agents and MCP Integration for Simulation',
      excerpt: 'How model context protocol enables AI agents to reason with simulation models. A technical deep-dive into the architecture powering intelligent decision support.',
      href: '/innovation/mcp-agents'
    },
    {
      category: 'Article',
      title: 'Why AI Needs Simulation (and Vice Versa)',
      excerpt: 'The symbiotic relationship between artificial intelligence and simulation modeling. Why predictive AI without causal understanding is incomplete—and how simulation fills the gap.',
      href: '/innovation/ai-meets-simulation'
    },
    {
      category: 'Article',
      title: 'From Operational Intelligence to Decision Intelligence',
      excerpt: 'Why dashboards and forecasts plateau—and how decision intelligence helps organizations choose actions that hold up under real-world pressure.',
      href: '/innovation/decision-intelligence',
      thumbnail: '/images/decision-intelligence-square.png'
    },
    {
      category: 'Article',
      title: 'Using Optimization with Simulation to Find the Best Path Forward',
      excerpt: 'How to move from exploring possibilities to identifying decisions that perform best across competing objectives and uncertain conditions.',
      href: '/innovation/optimization',
      thumbnail: '/images/optimization-square.png'
    },
    {
      category: 'Article',
      title: 'Designing Scalable, Replayable ETL for Modern Enterprises',
      excerpt: 'How to build decision-ready data architecture that powers analytics, simulation, and AI across the business.',
      href: '/innovation/decision-ready-data',
      thumbnail: '/images/decision-ready-data-square.png'
    },
    {
      category: 'Article',
      title: 'From Experience to Alignment: The System Dynamics Edge',
      excerpt: 'Why experiential learning is not enough on its own, and how system dynamics creates shared, testable mental models for better strategy.',
      href: '/innovation/experience-to-alignment',
      thumbnail: '/images/experiential_learning_square.png'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main className="max-w-4xl mx-auto px-8 py-16 md:py-24">
        {/* Hero */}
        <div className="mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Research & Innovation</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Ideas shaping the future of decision intelligence
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
            Original research, frameworks, and perspectives on simulation modeling, AI integration, 
            and the science of better decisions.
          </p>
        </div>

        {/* Featured Whitepaper */}
        <div className="mb-16 p-8 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Cover Image */}
            <div className="w-full md:w-48 flex-shrink-0">
              <img 
                src="/veydra-whitepaper---strategic-simulation-for-the-enterprise.jpg" 
                alt="Strategic Simulation for the Enterprise" 
                className="w-full rounded shadow-md"
              />
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Featured Whitepaper</span>
              <h2 className="text-2xl font-medium text-gray-900 mt-2 mb-3">
                Strategic Simulation for the Enterprise
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Dynamic Decision Modeling with the ERP Data Backbone. Learn how to extend ERP-derived truth 
                into forward-looking models that help organizations test policies, explore scenarios, and 
                quantify long-term impact with confidence.
              </p>
              <p className="text-gray-900 font-medium italic mb-6">
                "ERP provides truth. Veydra turns that truth into foresight."
              </p>
              <Link 
                to="/innovation/strategic-simulation"
                className="inline-flex items-center bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Read & Download
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Article List */}
        <div className="mb-8">
          <h2 className="text-xl font-medium text-gray-900 mb-6">More Research</h2>
        </div>

        <div className="space-y-8">
          {articles.map((article, index) => (
            <article key={index} className="group">
              <Link to={article.href} className="block">
                <div className={`flex ${article.thumbnail ? 'flex-col md:flex-row gap-6' : ''}`}>
                  {article.thumbnail && (
                    <div className="w-full md:w-32 flex-shrink-0">
                      <img 
                        src={article.thumbnail} 
                        alt={article.title}
                        className="w-full md:w-32 h-auto rounded-lg"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{article.category}</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-gray-600 mb-2 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-2">
                      {article.excerpt}
                    </p>
                    <span className="text-sm font-medium text-gray-900 group-hover:text-gray-600">
                      Read more →
                    </span>
                  </div>
                </div>
              </Link>
              
              {index < articles.length - 1 && (
                <div className="border-b border-gray-100 mt-8" />
              )}
            </article>
          ))}
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default InnovationHub;
